import { useState, useEffect, useRef } from 'react';
import { useScroll, useMotionValueEvent } from 'framer-motion';
import { usePopoverCloseSignal } from '@/stores';

interface UseScrollVisibilityOptions {
    threshold?: number;
    enabled?: boolean;
}

export function useScrollVisibility(
    container: HTMLElement | null,
    options?: UseScrollVisibilityOptions,
) {
    const { threshold = 50, enabled = true } = options || {};
    const [isVisible, setIsVisible] = useState(true);
    const containerRef = useRef<HTMLElement | null>(null);
    const trigger = usePopoverCloseSignal(
        (state: { signal: number; trigger: () => void }) => state.trigger,
    );

    useEffect(() => {
        containerRef.current = container;
    }, [container]);

    const { scrollY } = useScroll({
        container: container && enabled ? { current: container } : undefined,
    });

    useMotionValueEvent(scrollY, 'change', (latest) => {
        if (!container || !enabled) return;
        const prev = scrollY.getPrevious();
        if (prev === undefined) return;

        if (latest > prev && latest > threshold) {
            setIsVisible(false);
            trigger();
        } else if (latest < prev) {
            setIsVisible(true);
        }
    });

    return { isVisible, isReady: !!container };
}
